import type { Diagram, ModelDocument, Operation, Preferences } from '../../shared/contracts.js';
import { effectiveDetailLevel, effectiveEntityDisplay } from '../../shared/appearance.js';
import { entityFields, entitySize, resolveModelTypePreferences, vocabulary } from '../../shared/entityPresentation.js';
import { connectorUpdatesForMovedBoxes, overlaps, type CanvasBox } from './canvasGeometry.js';
import { resolveEntityPlacement } from './canvasPlacement.js';

/** Remove unused strips without reordering centers or changing a box's dimensions.
 * Axis constraints retain every original separated pair. Consequently a pair cannot
 * become an overlap, even when it was originally separated on only one axis. */
export function compactBoxes(boxes: readonly CanvasBox[], gap = 80): CanvasBox[] {
  const result = boxes.map(box => ({ ...box }));
  for (const axis of ['x', 'y'] as const) {
    const size = axis === 'x' ? 'width' : 'height';
    const sorted = [...result].sort((a,b) => a[axis] - b[axis] || a.id.localeCompare(b.id));
    const old = new Map(sorted.map(box => [box.id, box[axis]]));
    const origin = Math.min(...sorted.map(box => box[axis]));
    for (let i=0;i<sorted.length;i++) {
      const box = sorted[i]!;
      if (box.pinned) continue;
      let next = origin;
      for (let j=0;j<i;j++) {
        const before = sorted[j]!, separation = old.get(box.id)! - old.get(before.id)! - before[size];
        // Keep overlapping projection groups recognizable; compact empty space.
        next = Math.max(next, before[axis] + (separation >= 0 ? before[size] + Math.min(gap,separation) : Math.min(old.get(box.id)!-old.get(before.id)!, gap)));
      }
      // A later pin also establishes a maximum. Moving toward the origin cannot
      // newly overlap that pin; retain original placement if inconsistent imports do.
      box[axis] = Math.min(old.get(box.id)!, next);
    }
  }
  if (result.some((box,i) => result.slice(i+1).some(other => overlaps(box,other))))
    return resolveEntityPlacement(boxes,result,result.filter(box=>!box.pinned).map(box=>box.id),true,40);
  return result;
}

/** Explicit Auto-fit is a one-time override of dimensions, never of saved locks
 * or display selections. Scope is the current diagram, and collision repair and
 * route adaptation are part of the caller's single history transaction. */
export function autoFitDiagram(document: ModelDocument, diagram: Diagram, preferences: Preferences, before: readonly CanvasBox[]) {
  const effective = resolveModelTypePreferences(preferences,document,diagram);
  const terms = vocabulary(document,diagram.representationId,undefined,diagram.perspective);
  const after = before.map(box => {
    const appearance = diagram.appearances.find(a=>a.appearanceId===box.id)!;
    const object = document.objects.find(o=>o.objectId===box.objectId)!;
    const { fields,keys } = entityFields(document,object.objectId);
    const size = entitySize(object,fields,keys,{...appearance,sizeLocked:false,width:undefined,height:undefined},effective,terms.conceptual,effectiveDetailLevel(effective,diagram),undefined,true,terms.physical,effectiveEntityDisplay(effective,diagram));
    return {...box,width:size.width,height:size.height};
  });
  return resolveEntityPlacement(before,after,after.filter(box=>{const old=before.find(b=>b.id===box.id)!;return box.width!==old.width||box.height!==old.height;}).map(box=>box.id),preferences.autoAdjustNearbyEntities!==false);
}

export function diagramGeometryOperations(document:ModelDocument,diagram:Diagram,before:readonly CanvasBox[],after:readonly CanvasBox[],preferences:Preferences):Operation[] {
  const operations:Operation[]=[];
  for(const box of after) {
    const old=before.find(b=>b.id===box.id)!;
    const appearance=diagram.appearances.find(a=>a.appearanceId===box.id)!;
    const positionChanged=box.x!==old.x||box.y!==old.y,sizeChanged=box.width!==old.width||box.height!==old.height;
    // Two edits in one atomic batch perform the explicit one-time override;
    // the final lock equals the user's original choice.
    if(sizeChanged&&(appearance.sizeLocked??(appearance.width!==undefined||appearance.height!==undefined)))operations.push({type:'appearance.update',diagramId:diagram.diagramId,appearanceId:box.id,changes:{sizeLocked:false}});
    if(positionChanged||sizeChanged) operations.push({type:'appearance.update',diagramId:diagram.diagramId,appearanceId:box.id,changes:{
      ...(positionChanged?{position:{x:box.x,y:box.y}}:{}),
      ...(sizeChanged?{width:box.width,height:box.height,sizeLocked:appearance.sizeLocked??(appearance.width!==undefined||appearance.height!==undefined)}:{})}});
  }
  for(const update of connectorUpdatesForMovedBoxes([...before],[...after],document.relationships.filter(r=>r.representationId===diagram.representationId),diagram.connectors,(preferences.preserveManualRoutes || preferences.autoAdjustRelationships===false)))
    operations.push({type:'connector.set',diagramId:diagram.diagramId,connector:{...diagram.connectors.find(c=>c.relationshipId===update.relationshipId),...update.changes,relationshipId:update.relationshipId}});
  return operations;
}
